class MonadicError extends Error{constructor(m,p,e,a,c){super(m);this.name='MonadicError';this.path=p;this.expected=e;this.actual=a;this.callStack=c}};
function typeError(p,e,v){const a=v===null?'null':typeof v;const err=new MonadicError('Expected '+e+" for '"+p+"', got "+a,p,e,a);const c=globalThis.__tjs?.getConfig?.();if(c?.logTypeErrors)console.error('[TJS TypeError] '+err.message);if(c?.throwTypeErrors)throw err;return err};
function isMonadicError(v){return v instanceof Error&&v.name==='MonadicError'&&'path' in v};
const __stack=[];function pushStack(n){__stack.push(n)}function popStack(){__stack.pop()}function getStack(){return[...__stack]};
function Type(d,p,e){const t={description:d,__runtimeType:true};if(typeof p==='function'){t.check=p;t.default=e??null}else{const ex=e??p;t.default=ex;t.check=v=>{if(ex===null)return true;return typeof v===typeof ex}}return t};
const __tjs = globalThis.__tjs?.createRuntime?.() ?? {typeError,isMonadicError,pushStack,popStack,getStack,Type};
/* tjs <- sync.ts */

/*#
# sync

`sync()` synchronizes state across the network in real-time. Pass it a
transport, options, and boxed proxies from `tosi()` — local changes are
throttled and sent as batched deltas, and inbound messages from other
clients are applied to the local state automatically.

```
import { tosi, sync } from 'tosijs'

const { game } = tosi({
  game: { players: {}, ball: { x: 0, y: 0 } }
})

const ws = new WebSocket('wss://my-server.example/sync')

const { disconnect } = await sync(
  websocketTransport(ws),
  { throttleInterval: 50 },
  game
)

// Later, to disconnect:
disconnect()
```

## Transport interface

`sync()` is transport-agnostic. You provide an object that satisfies
`SyncTransport`:

    interface SyncTransport {
      send(messages: SyncMessage[]): void
      onReceive(handler: (messages: SyncMessage[]) => void): void
      connect(): Promise<void> | void
      disconnect(): void
    }

    interface SyncMessage {
      path: string
      value: any
    }

Messages are **batched** — `send()` receives an array of accumulated
deltas flushed at the throttle interval, and `onReceive()` delivers
batches from the server.

## WebSocket transport helper

```
function websocketTransport(ws) {
  let handler = null
  let pingInterval = null

  return {
    connect() {
      return new Promise((resolve, reject) => {
        if (ws.readyState === WebSocket.OPEN) return resolve()
        ws.addEventListener('open', () => resolve(), { once: true })
        ws.addEventListener('error', reject, { once: true })
      })
    },
    send(messages) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(messages))
      }
    },
    onReceive(h) {
      handler = h
      ws.addEventListener('message', (event) => {
        handler(JSON.parse(event.data))
      })
      // Keep alive: send empty batch periodically so the server
      // knows we're still here (see idleTimeout in sync-server.ts)
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('[]')
      }, 15000)
    },
    disconnect() {
      clearInterval(pingInterval)
      ws.close()
    },
  }
}
```

## Firebase Realtime Database transport

For Firebase, implement `SyncTransport` using `onValue` for inbound
and `update` for outbound. This is a sketch — adapt to your data model:

```
import { ref, onValue, update } from 'firebase/database'

function firebaseTransport(db, rootPath) {
  let handler = null
  let unsubscribe = null

  return {
    connect() {
      const dbRef = ref(db, rootPath)
      unsubscribe = onValue(dbRef, (snapshot) => {
        const data = snapshot.val()
        if (data && handler) {
          // Convert Firebase snapshot to SyncMessage[]
          const messages = Object.entries(data).map(
            ([path, value]) => ({ path, value })
          )
          handler(messages)
        }
      })
    },
    send(messages) {
      const updates = {}
      for (const msg of messages) {
        updates[`${rootPath}/${msg.path}`] = msg.value
      }
      update(ref(db), updates)
    },
    onReceive(h) { handler = h },
    disconnect() { if (unsubscribe) unsubscribe() },
  }
}
```

## Server architecture

The transport carries `SyncMessage[]` arrays. The **server** decides:

- **Broadcasting**: relay deltas to other connected clients
- **Persistence**: store state for snapshot-on-connect
- **Conflict resolution**: last-write-wins, server timestamps, or
  custom logic — `sync()` is conflict-agnostic

For most realistic applications, use a custom socket server as the
single source of truth. See `examples/sync-server.ts` for a minimal
Bun WebSocket relay server.

## API

    sync(
      transport: SyncTransport,
      options: SyncOptions,
      ...proxies: (BoxedProxy | string)[]
    ): Promise<{ disconnect: () => void }>

**SyncOptions:**

- `throttleInterval` — outbound batch interval in ms (default: 100)

The returned `disconnect()` removes all observers and calls
`transport.disconnect()`.
*/

import { registry } from './registry'

import { getByPath, setByPath } from './by-path'

import { touch, observe, unobserve, updates } from './path-listener'

import { tosiPath } from './metadata'

import { throttle } from './throttle'

export const SyncMessage = Type('SyncMessage', undefined, { path: '', value: null })

export const SyncTransport = Type('SyncTransport')

export const SyncOptions = Type('SyncOptions', undefined, { throttleInterval: 0.0 })

const inboundPaths = new Set();

/* line 189 */
function findSyncedRoot(syncedPaths,changedPath = '') {
  try {
  __tjs.pushStack('sync.ts:189:findSyncedRoot');
  if (syncedPaths instanceof Error) return syncedPaths;
  if (changedPath instanceof Error) return changedPath;
  if (changedPath !== undefined && typeof changedPath !== 'string') return __tjs.typeError('sync.ts:189:findSyncedRoot.changedPath', 'string', changedPath);

    for (const synced of syncedPaths) {
        if (changedPath === synced || changedPath.startsWith(synced + '.')) {
            return synced;
        }
    }
    return undefined;

  } finally { __tjs.popStack(); }
}
findSyncedRoot.__tjs = {
  "params": {
    "syncedPaths": {
      "type": {
        "kind": "any"
      },
      "required": true,
      "default": null
    },
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": false,
      "default": ""
    }
  },
  "source": "sync.ts:189"
}

/* line 201 */
function isInbound(changedPath) {
  try {
  __tjs.pushStack('sync.ts:201:isInbound');
  if (changedPath instanceof Error) return changedPath;
  if (typeof changedPath !== 'string') return __tjs.typeError('sync.ts:201:isInbound.changedPath', 'string', changedPath);

    for (const path of inboundPaths) {
        if (changedPath === path || changedPath.startsWith(path + '.')) {
            return true;
        }
    }
    return false;

  } finally { __tjs.popStack(); }
}
isInbound.__tjs = {
  "params": {
    "changedPath": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "returns": {
    "type": {
      "kind": "boolean"
    }
  },
  "unsafeReturn": true,
  "source": "sync.ts:201"
}

/* line 210 */
function applyInbound(path, value) {
  try {
  __tjs.pushStack('sync.ts:210:applyInbound');
  if (path instanceof Error) return path;
  if (value instanceof Error) return value;
  if (typeof path !== 'string') return __tjs.typeError('sync.ts:210:applyInbound.path', 'string', path);

    inboundPaths.add(path);
    setByPath(registry, path, value);
    touch(path);
    updates().then(() => {
        inboundPaths.delete(path);
    });

  } finally { __tjs.popStack(); }
}
applyInbound.__tjs = {
  "params": {
    "path": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "value": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "source": "sync.ts:210"
}

/* line 219 */
export async function sync(transport,options,...proxies) {
  try {
  __tjs.pushStack('sync.ts:207:sync');
  if (transport instanceof Error) return transport;
  if (options instanceof Error) return options;
  if (proxies instanceof Error) return proxies;
  if ((typeof transport !== 'object' || transport === null || Array.isArray(transport))) return __tjs.typeError('sync.ts:207:sync.transport', 'object', transport);
  if ((typeof options !== 'object' || options === null || Array.isArray(options))) return __tjs.typeError('sync.ts:207:sync.options', 'object', options);
  if (proxies !== undefined && !Array.isArray(proxies)) return __tjs.typeError('sync.ts:207:sync.proxies', 'array', proxies);

    const syncedPaths = new Set();
    const outboundBatch = [];
    const activeListeners = [];
    const interval = options.throttleInterval ?? 100;
    await transport.connect();
    // Throttled flush of accumulated outbound deltas
    const flushOutbound = throttle(() => {
        if (outboundBatch.length === 0)
            return;
        const batch = outboundBatch.splice(0);
        transport.send(batch);
    }, interval);
    // Register inbound handler
    transport.onReceive((messages) => {
        for (const msg of messages) {
            if (findSyncedRoot(syncedPaths, msg.path) === undefined)
                continue;
            applyInbound(msg.path, msg.value);
        }
    });
    // Register outbound observers for each proxy/path
    for (const proxy of proxies) {
        const path = typeof proxy === 'string' ? proxy : tosiPath(proxy);
        if (path === undefined) {
            throw new Error('sync() requires boxed proxies or string paths. Got a non-proxy value.');
        }
        syncedPaths.add(path);
        const listener = observe((changedPath) => changedPath === path || changedPath.startsWith(path + '.'), (changedPath) => {
            if (isInbound(changedPath))
                return;
            if (findSyncedRoot(syncedPaths, changedPath) === undefined)
                return;
            const value = getByPath(registry, changedPath);
            outboundBatch.push({ path: changedPath, value });
            flushOutbound();
        });
        activeListeners.push(listener);
    }
    return {
        disconnect() {
            for (const listener of activeListeners) {
                unobserve(listener);
            }
            activeListeners.length = 0;
            syncedPaths.clear();
            outboundBatch.length = 0;
            transport.disconnect();
        },
    };

  } finally { __tjs.popStack(); }
}
sync.__tjs = {
  "params": {
    "transport": {
      "type": {
        "kind": "object",
        "shape": {}
      },
      "required": true,
      "default": null
    },
    "options": {
      "type": {
        "kind": "object",
        "shape": {
          "throttleInterval": {
            "kind": "number"
          }
        }
      },
      "required": true,
      "default": null
    },
    "proxies": {
      "type": {
        "kind": "array"
      },
      "required": false
    }
  },
  "returns": {
    "type": {
      "kind": "object",
      "shape": {
        "disconnect": {
          "kind": "any"
        }
      }
    }
  },
  "unsafeReturn": true,
  "source": "sync.ts:207"
}